import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PaymentModal from "../components/PaymentModal";
import { addPurchasePayment, getPurchaseById, updatePurchase, markAsPaid, sendPrintRequest, getBranchTaxes, resumePurchase, reopenPurchase } from "../api"; // Replace with your API function
import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";
import useCurrentUser from "../hooks/useCurrentUser";
import useMyPreferences from "../hooks/useMyPreferences";
import { hasPermission } from "../utils/permissions";
import InlineLoading from "../components/InlineLoading";
import Barcode from "../components/Barcode";
import { getPurchaseItems } from "../utils/purchaseItems";
import { notify } from '../utils/alertBus';
import { getPrintMode, PRINT_MODE_REMOTE } from '../utils/printSettings';
import { normalizePrintReceiptRedirect } from "../utils/userPreferences";
import {
  buildReceiptItemLine,
  buildReceiptCategoryTotals,
  formatVoucherQuantity,
} from '../utils/receiptFormatting';

const AUTO_PRINT_DELAY_MS = 1200;

const isFacturaDocument = (purchase) =>
  String(purchase?.documentType || '').trim().toLowerCase() === 'factura';

const resolveTaxExclusiveLineAmounts = ({ item, grossLineTotal, quantity, netFallbackRatio }) => {
  const persistedNet = Number(item?.netAmount);
  let netLineTotal = Number.isFinite(persistedNet) && persistedNet >= 0
    ? Math.round(persistedNet)
    : null;

  if (netLineTotal === null) {
    const persistedTax = Number(item?.taxAmount);
    if (Number.isFinite(persistedTax) && persistedTax >= 0) {
      netLineTotal = Math.max(Math.round(grossLineTotal - persistedTax), 0);
    }
  }

  if (netLineTotal === null) {
    netLineTotal = Math.max(Math.round(grossLineTotal * netFallbackRatio), 0);
  }

  const unitNetPrice = quantity > 0 ? Math.round(netLineTotal / quantity) : netLineTotal;
  return { netLineTotal, unitNetPrice };
};

const PurchaseReceiptPage = ({ previewOnly = false }) => {
  const { purchaseId } = useParams(); // Get the purchase ID from the URL
  const [purchase, setPurchase] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const pdfRef = useRef(null);
  const pdfIframeRef = useRef(null);
  const handlePrintRef = useRef(null);
  const autoPrintDoneRef = useRef(false);
  const autoPrintTimerRef = useRef(null);
  const postPrintFlowTimerRef = useRef(null);
  const redirectTimerRef = useRef(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [receiptWidth, setReceiptWidth] = useState("80"); // Default to 80mm
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const [isResumingPurchase, setIsResumingPurchase] = useState(false);
  const [isCategoryBarcodeModalOpen, setIsCategoryBarcodeModalOpen] = useState(false);
  const [categoryBarcodeModalRows, setCategoryBarcodeModalRows] = useState([]);
  const [categoryBarcodeModalRedirectSeconds, setCategoryBarcodeModalRedirectSeconds] = useState(null);
  const location = useLocation();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();
  const { preferences: myPreferences } = useMyPreferences({ enabled: !previewOnly });
  const printReceiptRedirect = normalizePrintReceiptRedirect(myPreferences?.printReceiptRedirect);
  const canReadSales = hasPermission(user, "sales.read");
  const canUpdateSale = hasPermission(user, "sales.update");
  const canReadClient = hasPermission(user, "client.read");
  const canCreateClient = hasPermission(user, "client.create");
  const canReadTaxes = hasPermission(user, "tax.read") || hasPermission(user, "tax.exclude");
  const canCreateDeliveryGuide = hasPermission(user, "delivery_guide.create");
  const receiptParams = new URLSearchParams(location.search);
  const autoPrintParam = receiptParams.get("autoprint");
  const temporaryQuickFinalizeParam = receiptParams.get("temporaryQuickFinalize");
  const fromPurchaseOrderParam = receiptParams.get("fromPurchaseOrder");
  const offerDeliveryGuideParam = receiptParams.get("offerDeliveryGuide");
  const shouldAutoPrint = autoPrintParam === "1" || autoPrintParam === "true";
  const isTemporaryQuickFinalizeReceipt =
    temporaryQuickFinalizeParam === "1" || temporaryQuickFinalizeParam === "true";

  useEffect(() => {
    autoPrintDoneRef.current = false;
    if (autoPrintTimerRef.current) {
      clearTimeout(autoPrintTimerRef.current);
      autoPrintTimerRef.current = null;
    }
    if (postPrintFlowTimerRef.current) {
      clearTimeout(postPrintFlowTimerRef.current);
      postPrintFlowTimerRef.current = null;
    }
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    setIsCategoryBarcodeModalOpen(false);
    setCategoryBarcodeModalRows([]);
    setCategoryBarcodeModalRedirectSeconds(null);
  }, [purchaseId, autoPrintParam]);

  useEffect(() => {
    setIsPdfLoaded(false);
  }, [pdfUrl]);

  const buildBarcodeDataUrl = (value, {
    format = "CODE128",
    height = 40,
    width = 1.2,
    margin = 0,
    marginTop = margin,
    marginBottom = margin,
    marginLeft = margin,
    marginRight = margin,
  } = {}) => {
    const normalized = String(value || "").trim();
    if (!normalized) return null;
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, normalized, {
        format,
        displayValue: false,
        height,
        width,
        margin,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,
        lineColor: "#000000",
        background: "#ffffff",
      });
      return canvas.toDataURL("image/png");
    } catch (error) {
      console.warn("No se pudo generar el barcode:", error);
      return null;
    }
  };

  const buildCategoryPrintBarcodeDataUrl = (value) => buildBarcodeDataUrl(value, {
    format: "CODE128",
    height: 160,
    width: 4,
    margin: 32,
    marginTop: 4,
    marginBottom: 4,
  });

  const getCategoryPrintBarcodeSize = (availableDocWidth) => ({
    width: Math.min(receiptWidth < 80 ? 42 : 58, Math.max(availableDocWidth - 6, 1)),
    height: receiptWidth < 80 ? 18 : 20,
  });

  const navigate = useNavigate();

  const buildCategoryBarcodeModalRows = (sourcePurchase) => {
    if (!sourcePurchase) return [];
    const purchaseItems = getPurchaseItems(sourcePurchase);
    return buildReceiptCategoryTotals({ purchase: sourcePurchase, purchaseItems });
  };

  const clearPendingPrintRedirect = () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    setCategoryBarcodeModalRedirectSeconds(null);
  };

  const clearPendingPostPrintFlow = () => {
    if (postPrintFlowTimerRef.current) {
      clearTimeout(postPrintFlowTimerRef.current);
      postPrintFlowTimerRef.current = null;
    }
  };

  const openCategoryBarcodeModal = (sourcePurchase) => {
    setCategoryBarcodeModalRows(buildCategoryBarcodeModalRows(sourcePurchase));
    setIsCategoryBarcodeModalOpen(true);
  };

  const schedulePrintRedirect = () => {
    clearPendingPrintRedirect();
    const shouldRedirectAfterPrint = printReceiptRedirect.enabled || isTemporaryQuickFinalizeReceipt;
    if (previewOnly || !shouldRedirectAfterPrint) return;

    const delaySeconds = printReceiptRedirect.delaySeconds;
    setCategoryBarcodeModalRedirectSeconds(delaySeconds);
    redirectTimerRef.current = setTimeout(() => {
      redirectTimerRef.current = null;
      navigate(`/branches/${branchId}/purchases/new`);
    }, delaySeconds * 1000);
  };

  const handleCloseCategoryBarcodeModal = () => {
    clearPendingPrintRedirect();
    setIsCategoryBarcodeModalOpen(false);
  };

  const handleGoBackToPos = () => {
    clearPendingPrintRedirect();
    navigate(`/branches/${branchId}/purchases/new`);
  };

  const getCategoryBarcodeNote = (categoryTotal) => {
    if (categoryTotal?.barcode?.isValid) return null;
    return categoryTotal?.barcode?.reason === "amount"
      ? "monto excede codigo"
      : "categoria sin barcode";
  };

  useEffect(() => () => {
    if (autoPrintTimerRef.current) {
      clearTimeout(autoPrintTimerRef.current);
    }
    if (postPrintFlowTimerRef.current) {
      clearTimeout(postPrintFlowTimerRef.current);
    }
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (categoryBarcodeModalRedirectSeconds === null || categoryBarcodeModalRedirectSeconds <= 0) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      setCategoryBarcodeModalRedirectSeconds((seconds) => (
        seconds === null ? null : Math.max(seconds - 1, 0)
      ));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [categoryBarcodeModalRedirectSeconds]);

  const normalizePurchasePayload = (data) => {
    if (!data) return data;
    const normalized = { ...data };
    if (typeof normalized.detailForPayment === "string") {
      try {
        normalized.detailForPayment = JSON.parse(normalized.detailForPayment);
      } catch (error) {
        console.warn("No se pudo parsear detailForPayment:", error);
        normalized.detailForPayment = {};
      }
    }
    return normalized;
  };

  const cleanPaymentDetails = (details) => Object.entries(details || {}).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) return acc;
    if (typeof value === "string" && value.trim() === "") return acc;
    acc[key] = typeof value === "string" ? value.trim() : value;
    return acc;
  }, {});

  const refreshPurchaseData = async ({ autoOpenPayment = false } = {}) => {
    const raw = await getPurchaseById(branchId, purchaseId);
    const data = normalizePurchasePayload(raw);
    setPurchase(data);
    generatePDF(data);
    const paidStatus = data.paymentStatus === "paid" || data.isPaid;
    setIsPaid(paidStatus);
    if (autoOpenPayment) {
      const isSuspended = data.saleStatus === "suspended";
      setIsPaymentModalOpen(!previewOnly && !paidStatus && canUpdateSale && !isSuspended);
    }
    return data;
  };

  useEffect(() => {
    const fetchPurchase = async () => {
      try {
        await refreshPurchaseData({ autoOpenPayment: true });
      } catch (error) {
        console.error("Error fetching purchase:", error);
      }
    };

    fetchPurchase();
  }, [purchaseId, branchId, canUpdateSale, previewOnly]);

  useEffect(() => {
    if (!branchId || !canReadTaxes) {
      setTaxes([]);
      return;
    }
    let isMounted = true;
    const loadTaxes = async () => {
      try {
        const data = await getBranchTaxes(branchId);
        if (!isMounted) return;
        setTaxes(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching taxes:", error);
        setTaxes([]);
      }
    };
    loadTaxes();
    return () => {
      isMounted = false;
    };
  }, [branchId, canReadTaxes]);

  const handleConfirmPayment = async (
    method,
    details,
    paymentTerm,
    nextDocumentType,
    nextClientId
  ) => {
    try {
      if (!canUpdateSale) return;
      const totalAmount = Number(purchase?.totalAmount || 0);
      const currentPaidAmount = Number(purchase?.paidAmount || 0);
      const currentBalance =
        purchase?.balance !== undefined
          ? Number(purchase.balance || 0)
          : Math.max(totalAmount - currentPaidAmount, 0);
      const isCredit = paymentTerm === "credit";
      const cleanedDetails = cleanPaymentDetails(details);
      const initialPayment = Number(cleanedDetails.initialPayment || 0);
      let paymentAmount = 0;
      let paymentMethod = method;
      const paymentPayload = {};
      const resolvedDocumentType =
        nextDocumentType === "boleta" || nextDocumentType === "factura"
          ? nextDocumentType
          : null;

      if (isCredit) {
        paymentMethod = cleanedDetails.initialPaymentMethod || "efectivo";
        paymentAmount = Math.min(Math.max(initialPayment, 0), currentBalance);
        paymentPayload.paymentTerm = "credit";
        paymentPayload.creditDueDate = cleanedDetails.creditDueDate || null;
        paymentPayload.initialPayment = paymentAmount;
        paymentPayload.paymentMethod = null;
        paymentPayload.isPaid = currentPaidAmount + paymentAmount >= totalAmount && totalAmount > 0;
        paymentPayload.detailForPayment = {
          ...cleanedDetails,
          creditDueDate: cleanedDetails.creditDueDate || null,
        };
      } else {
        if (method === "efectivo") {
          const hasReceivedAmount =
            cleanedDetails.receivedAmount !== undefined
            && cleanedDetails.receivedAmount !== null
            && String(cleanedDetails.receivedAmount).trim() !== "";
          const received = hasReceivedAmount ? Number(cleanedDetails.receivedAmount || 0) : currentBalance;
          paymentAmount = Math.min(received, currentBalance);
        } else {
          paymentAmount = currentBalance;
        }
        paymentPayload.paymentTerm = "immediate";
        paymentPayload.paymentMethod = method;
        paymentPayload.isPaid = currentPaidAmount + paymentAmount >= totalAmount && totalAmount > 0;
        paymentPayload.detailForPayment = cleanedDetails;
      }

      if (resolvedDocumentType) {
        paymentPayload.documentType = resolvedDocumentType;
      }
      if (purchase?.saleStatus === "open") {
        paymentPayload.saleStatus = "completed";
      }
      if (Number.isInteger(nextClientId)) {
        paymentPayload.clientId = nextClientId;
      }

      const response = await updatePurchase(branchId, purchaseId, paymentPayload);
      if (response?.warning) {
        notify(response.warning);
      }

      if (paymentAmount > 0 && paymentMethod) {
        const reference =
          method === "transferencia" || method === "tarjeta"
            ? cleanedDetails.transactionNumber
            : method === "cheque"
              ? cleanedDetails.chequeNumber
              : undefined;
        const note =
          method === "cheque" && cleanedDetails.chequeExpiration
            ? `Expira: ${cleanedDetails.chequeExpiration}`
            : undefined;
        await addPurchasePayment(branchId, purchaseId, {
          amount: paymentAmount,
          method: paymentMethod,
          reference,
          note,
        });
      }

      await refreshPurchaseData({ autoOpenPayment: false });
      setIsPaymentModalOpen(false);
    } catch (error) {
      console.error("Error al registrar el pago:", error);
      notify("Error al registrar el pago.");
    }
  };

  useEffect(() => {
    if (purchase) {
      generatePDF(purchase);
    }
  }, [receiptWidth, taxes, purchase]);

  const generatePDF = (purchase) => {
    if (!purchase) return;

    const purchaseItems = getPurchaseItems(purchase);
    const categoryTotals = buildReceiptCategoryTotals({ purchase, purchaseItems });
    const isSuspended = purchase.saleStatus === "suspended";
    const suspendedCode = isSuspended ? purchase.suspendedCode : null;
    const excludedTaxIds = Array.isArray(purchase.excludedTaxIds) ? purchase.excludedTaxIds : [];
    const taxNameById = new Map(
      (taxes || []).map((tax) => [Number(tax.id), tax.name || `Impuesto ${tax.id}`])
    );
    const excludedLabels = excludedTaxIds
      .map((id) => taxNameById.get(Number(id)) || `#${id}`)
      .filter(Boolean);
    const excludedNote =
      excludedLabels.length > 0 ? `Impuestos excluidos: ${excludedLabels.join(", ")}` : null;
    const breakdown = Array.isArray(purchase.taxBreakdown) ? purchase.taxBreakdown : [];
    const printableTaxBreakdown = breakdown.filter((row) => Number(row?.amount || 0) > 0);
    const hasSingleTaxLine = printableTaxBreakdown.length === 1;
    const showFacturaTaxTotal = !hasSingleTaxLine;
    const grossSubtotal = purchaseItems.reduce((sum, item) => {
      const qty = Number(item?.quantity || 0);
      if (qty <= 0) return sum;
      const price = Number(item?.price || 0);
      return sum + Math.floor(price * qty);
    }, 0);
    const itemDiscountAmount = purchaseItems.reduce((sum, item) => {
      const qty = Number(item?.quantity || 0);
      if (qty <= 0) return sum;
      const discountPerUnit = Number(item?.discount || 0);
      return sum + Math.floor(discountPerUnit * qty);
    }, 0);
    const subtotalAfterItemDiscount =
      typeof purchase.itemsSubtotal === "number"
        ? purchase.itemsSubtotal
        : Math.max(grossSubtotal - itemDiscountAmount, 0);
    const discountPercentNum = Number(purchase.orderDiscountPercentage);
    const discountOrderPercent = Number.isFinite(discountPercentNum)
      ? discountPercentNum
      : null;
    const discountAmountNum = Number(purchase.orderDiscountAmount);
    const discountOrderAmount =
      discountOrderPercent !== null
        ? Math.floor(subtotalAfterItemDiscount * (discountOrderPercent / 100))
        : Number.isFinite(discountAmountNum)
          ? discountAmountNum
          : 0;
    const totalDiscountAmount = itemDiscountAmount + Math.max(discountOrderAmount, 0);
    const taxTotal = Number.isFinite(Number(purchase.taxTotal))
      ? Number(purchase.taxTotal)
      : breakdown.reduce((sum, row) => sum + (Number(row?.amount || 0)), 0);
    const totalAmount = Number(purchase.totalAmount || 0);
    const netTotal = Number.isFinite(Number(purchase.netTotal))
      ? Number(purchase.netTotal)
      : Math.max(totalAmount - taxTotal, 0);
    const facturaReceipt = isFacturaDocument(purchase);
    const netFallbackRatio = totalAmount > 0 ? netTotal / totalAmount : 1;
    const docWidth = receiptWidth - 10;
    const textAreaWidth = docWidth - 28; // leave room for right-aligned numbers
    const marginTop = 5;
    const lineHeight = 5;
    let estimatedHeight = marginTop + 30;

    estimatedHeight += purchase.Client ? 35 : 10;
    estimatedHeight += 10;

    purchaseItems.forEach((item) => {
      if (item.quantity <= 0) return;
      estimatedHeight += lineHeight * 3; // base line + net/total line
      if (!facturaReceipt && item.discount && item.discount > 0) {
        estimatedHeight += lineHeight; // extra line for discount
      }
    });

    if (facturaReceipt) {
      estimatedHeight += lineHeight; // Neto
      if (totalDiscountAmount > 0) estimatedHeight += lineHeight;
      if (printableTaxBreakdown.length) estimatedHeight += printableTaxBreakdown.length * lineHeight;
      if (showFacturaTaxTotal) estimatedHeight += lineHeight; // Total impuestos
    } else {
      estimatedHeight += lineHeight; // Subtotal
      if (totalDiscountAmount > 0) estimatedHeight += lineHeight;
      estimatedHeight += lineHeight; // Neto
      if (hasSingleTaxLine) {
        estimatedHeight += lineHeight; // One tax line
      } else {
        estimatedHeight += lineHeight; // Impuestos total
        if (printableTaxBreakdown.length) estimatedHeight += printableTaxBreakdown.length * lineHeight;
      }
    }
    estimatedHeight += lineHeight; // Total
    if (excludedNote) {
      estimatedHeight += Math.ceil(excludedNote.length / 28) * lineHeight;
    }

    if (categoryTotals.length) {
      estimatedHeight += lineHeight + 2;
      categoryTotals.forEach((categoryTotal) => {
        const label = `${categoryTotal.label}: $${Number(categoryTotal.amount || 0).toLocaleString("de-DE")}`;
        estimatedHeight += Math.max(1, Math.ceil(label.length / 30)) * lineHeight;
        estimatedHeight += categoryTotal.barcode?.isValid
          ? (receiptWidth < 80 ? 18 : 20) + lineHeight + 3
          : lineHeight;
      });
    }

    if (isSuspended && suspendedCode) {
      estimatedHeight += receiptWidth < 80 ? 22 : 28;
    }

    estimatedHeight += 30;
    const pageHeight = receiptWidth < 80 ? Math.max(estimatedHeight * 1.5, 80) : Math.max(estimatedHeight, 80);
    let currentY = marginTop;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [docWidth, pageHeight],
    });

    doc.setFontSize(10);
    doc.text(isSuspended ? "Venta Suspendida" : "Comprobante de Venta", docWidth / 2, currentY, { align: "center" });
    currentY += lineHeight + 2;

    doc.setFontSize(9);
    doc.text("Nro Venta: " + purchase.id, 5, currentY);
    if (receiptWidth === 58) currentY += lineHeight;
    doc.setFontSize(7);
    doc.text("Fecha: " + new Date(purchase.createdAt).toLocaleString(), docWidth - 5, currentY, { align: "right" });
    currentY += lineHeight + 2;

    if (isSuspended && suspendedCode) {
      doc.setFontSize(7);
      doc.text("Voucher para reanudar venta", docWidth / 2, currentY, { align: "center" });
      currentY += lineHeight + 1;
      const barcodeDataUrl = buildBarcodeDataUrl(suspendedCode);
      if (barcodeDataUrl) {
        const barcodeWidth = Math.max(docWidth - 12, 40);
        const barcodeHeight = receiptWidth < 80 ? 14 : 18;
        const barcodeX = (docWidth - barcodeWidth) / 2;
        doc.addImage(barcodeDataUrl, "PNG", barcodeX, currentY, barcodeWidth, barcodeHeight);
        currentY += barcodeHeight + 2;
        doc.text(suspendedCode, docWidth / 2, currentY, { align: "center" });
        currentY += lineHeight + 1;
      }
    }

    if (purchase.Client) {
      const { name, rut, email, address, city, phone } = purchase.Client;
      doc.setFontSize(receiptWidth < 80 ? 6 : 8);
      doc.text(`Nombre: ${name || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`RUT: ${rut || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Email: ${email || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Dirección: ${address || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Ciudad: ${city || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Teléfono: ${phone || "N/A"}`, 5, currentY); currentY += lineHeight;
    } else {
      doc.setFontSize(8);
      doc.text("Venta Detalle (sin cliente)", 5, currentY); currentY += lineHeight;
    }

    doc.setFontSize(8);
    doc.text("Detalle de la Venta:", 5, currentY); currentY += lineHeight + 2;

    purchaseItems.forEach((item) => {
      if (item.quantity <= 0) return;

      const hasDiscount = item.discount && item.discount > 0;
      const originalPrice = Number(item.price) || 0;
      const discountPerUnit = Number(item.discount || 0);
      const quantity = Number(item.quantity || 0);
      const quantityLabel = formatVoucherQuantity(item.quantity);
      const lineGrossTotal = Math.floor(originalPrice * quantity);
      const totalDiscount = Math.floor(discountPerUnit * quantity);
      const percentage =
        hasDiscount && originalPrice > 0
          ? Math.round((discountPerUnit / originalPrice) * 100)
          : null;
      const effectiveGrossLineTotal = Math.max(lineGrossTotal - totalDiscount, 0);
      const facturaLineAmounts = facturaReceipt
        ? resolveTaxExclusiveLineAmounts({
          item,
          grossLineTotal: effectiveGrossLineTotal,
          quantity,
          netFallbackRatio,
        })
        : null;
      const displayUnitPrice = facturaReceipt ? facturaLineAmounts.unitNetPrice : originalPrice;
      const displayLineTotal = facturaReceipt ? facturaLineAmounts.netLineTotal : lineGrossTotal;

      // 🧾 Line 1: Product at full price
      const line = buildReceiptItemLine({
        item,
        unitPrice: displayUnitPrice,
        quantityLabel,
      });
      const subtotal = `$${displayLineTotal.toLocaleString("de-DE")}`;

      // Break line into chunks of ~25 characters (you can adjust the width in mm)
      const wrappedLines = doc.splitTextToSize(line, textAreaWidth);
      wrappedLines.forEach((part, idx) => {
        const yOffset = currentY + idx * lineHeight;
        doc.text(part, 6, yOffset);
        if (idx === wrappedLines.length - 1) {
          doc.text(subtotal, docWidth - 5 - doc.getTextWidth(subtotal), yOffset);
        }
      });
      currentY += wrappedLines.length * lineHeight;

      // 🧾 Line 2: Discount (aligned)
      if (!facturaReceipt && hasDiscount) {
        doc.setFontSize(7);
        const discountDesc = `Descuento${percentage !== null ? ` (${percentage}%)` : ""}`;
        const discountTotal = `- $${totalDiscount.toLocaleString("de-DE")}`;
        const discountLines = doc.splitTextToSize(discountDesc, textAreaWidth);
        discountLines.forEach((part, idx) => {
          const yOffset = currentY + idx * lineHeight;
          doc.text(part, 6, yOffset);
          if (idx === discountLines.length - 1) {
            doc.text(discountTotal, docWidth - 5 - doc.getTextWidth(discountTotal), yOffset);
          }
        });
        currentY += discountLines.length * lineHeight;
        doc.setFontSize(8);
      }
    });

    currentY += 2;
    doc.setFontSize(9);

    if (facturaReceipt) {
      const netText = `Neto: $${netTotal.toLocaleString("de-DE")}`;
      doc.text(netText, docWidth - 10 - doc.getTextWidth(netText), currentY);
      currentY += lineHeight;

      if (totalDiscountAmount > 0) {
        const discountLabel = `Descuentos aplicados${
          discountOrderPercent != null ? ` (orden ${discountOrderPercent}%)` : ""
        }: -$${totalDiscountAmount.toLocaleString("de-DE")}`;
        doc.text(discountLabel, docWidth - 10 - doc.getTextWidth(discountLabel), currentY);
        currentY += lineHeight;
      }

      if (printableTaxBreakdown.length) {
        printableTaxBreakdown.forEach((row) => {
          const label = `${row.name || "Impuesto"}${row.rate ? ` (${row.rate}%)` : ""}: $${Number(row.amount || 0).toLocaleString("de-DE")}`;
          doc.text(label, docWidth - 10 - doc.getTextWidth(label), currentY);
          currentY += lineHeight;
        });
      }

      if (showFacturaTaxTotal) {
        const taxesText = `Total impuestos: $${taxTotal.toLocaleString("de-DE")}`;
        doc.text(taxesText, docWidth - 10 - doc.getTextWidth(taxesText), currentY);
        currentY += lineHeight;
      }
    } else {
      const subtotalText = `Subtotal: $${grossSubtotal.toLocaleString("de-DE")}`;
      doc.text(subtotalText, docWidth - 10 - doc.getTextWidth(subtotalText), currentY);
      currentY += lineHeight;

      if (totalDiscountAmount > 0) {
        const discountLabel = `Descuentos${
          discountOrderPercent != null ? ` (orden ${discountOrderPercent}%)` : ""
        }: -$${totalDiscountAmount.toLocaleString("de-DE")}`;
        doc.text(discountLabel, docWidth - 10 - doc.getTextWidth(discountLabel), currentY);
        currentY += lineHeight;
      }

      const netText = `Valor neto: $${netTotal.toLocaleString("de-DE")}`;
      doc.text(netText, docWidth - 10 - doc.getTextWidth(netText), currentY);
      currentY += lineHeight;

      if (hasSingleTaxLine) {
        const row = printableTaxBreakdown[0];
        const singleTaxLabel = `${row.name || "Impuesto"}${row.rate ? ` (${row.rate}%)` : ""}: $${Number(row.amount || 0).toLocaleString("de-DE")}`;
        doc.text(singleTaxLabel, docWidth - 10 - doc.getTextWidth(singleTaxLabel), currentY);
        currentY += lineHeight;
      } else {
        const taxesText = `Impuestos: $${taxTotal.toLocaleString("de-DE")}`;
        doc.text(taxesText, docWidth - 10 - doc.getTextWidth(taxesText), currentY);
        currentY += lineHeight;
      }

      if (!hasSingleTaxLine && printableTaxBreakdown.length) {
        printableTaxBreakdown.forEach((row) => {
          const label = `- ${row.name || "Impuesto"}${row.rate ? ` (${row.rate}%)` : ""}: $${Number(row.amount || 0).toLocaleString("de-DE")}`;
          doc.text(label, docWidth - 10 - doc.getTextWidth(label), currentY);
          currentY += lineHeight;
        });
      }
    }

    if (excludedNote) {
      const noteLines = doc.splitTextToSize(excludedNote, textAreaWidth);
      noteLines.forEach((part, idx) => {
        const yOffset = currentY + idx * lineHeight;
        doc.text(part, 6, yOffset);
      });
      currentY += noteLines.length * lineHeight;
    }

    const totalText = `Total: $${totalAmount.toLocaleString("de-DE")}`;
    doc.text(totalText, docWidth - 10 - doc.getTextWidth(totalText), currentY);
    currentY += lineHeight + 1;

    const method = purchase.paymentMethod || "No especificado";
    const detail = purchase.detailForPayment || {};
    const paymentTerm =
      purchase.paymentTerm === "credit" || method === "crédito"
        ? "credit"
        : "immediate";
    const paidAmount = Number(purchase.paidAmount || 0);
    const balance =
      purchase.balance !== undefined
        ? Number(purchase.balance || 0)
        : Math.max((Number(purchase.totalAmount || 0) - paidAmount), 0);
    const formatDate = (value) => {
      if (!value) return "N/A";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
    };

    doc.setFontSize(8);
    doc.text("Detalles del Pago:", 5, currentY); currentY += lineHeight;
    doc.text(`Condición: ${paymentTerm === "credit" ? "Crédito" : "Contado"}`, 5, currentY); currentY += lineHeight;
    if (paymentTerm === "immediate") {
      doc.text(`Método: ${method}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "immediate" && method === "cheque") {
      doc.text(`Cheque N°: ${detail.chequeNumber || "N/A"}`, 5, currentY); currentY += lineHeight;
      doc.text(`Expira: ${detail.chequeExpiration || "N/A"}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "immediate" && ["transferencia", "tarjeta"].includes(method)) {
      doc.text(`Transacción: ${detail.transactionNumber || "N/A"}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "immediate" && method === "efectivo") {
      const hasReceivedAmount =
        detail.receivedAmount !== undefined
        && detail.receivedAmount !== null
        && String(detail.receivedAmount).trim() !== "";
      const received = hasReceivedAmount ? parseInt(detail.receivedAmount, 10) : totalAmount;
      const change = Math.max(received - totalAmount, 0);
      doc.text(`Recibido: $${received.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
      doc.text(`Vuelto: $${change.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
    }

    if (paymentTerm === "credit") {
      const dueDate = formatDate(purchase.creditDueDate || detail.creditDueDate || detail.creditExpiration);
      doc.text(`Vence: ${dueDate}`, 5, currentY); currentY += lineHeight;
      const initialPayment = Number(purchase.initialPayment || detail.initialPayment || 0);
      if (initialPayment > 0) {
        doc.text(`Abono Inicial: $${initialPayment.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
      }
    }

    if (paidAmount > 0 || balance > 0) {
      doc.text(`Pagado: $${paidAmount.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
      doc.text(`Pendiente: $${balance.toLocaleString("de-DE")}`, 5, currentY); currentY += lineHeight;
    }

    if (categoryTotals.length) {
      currentY += 2;
      doc.setFontSize(8);
      doc.text("Totales por categoria:", 5, currentY);
      currentY += lineHeight;

      categoryTotals.forEach((categoryTotal) => {
        const label = `${categoryTotal.label}: $${Number(categoryTotal.amount || 0).toLocaleString("de-DE")}`;
        const labelLines = doc.splitTextToSize(label, docWidth - 10);
        labelLines.forEach((part) => {
          doc.text(part, 5, currentY);
          currentY += lineHeight;
        });

        if (categoryTotal.barcode?.isValid) {
          const barcodeDataUrl = buildCategoryPrintBarcodeDataUrl(categoryTotal.barcode.value);
          if (barcodeDataUrl) {
            const { width: barcodeWidth, height: barcodeHeight } = getCategoryPrintBarcodeSize(docWidth);
            const barcodeX = (docWidth - barcodeWidth) / 2;
            doc.addImage(barcodeDataUrl, "PNG", barcodeX, currentY, barcodeWidth, barcodeHeight);
            currentY += barcodeHeight + 3;
            doc.setFontSize(6);
            doc.text(categoryTotal.barcode.value, docWidth / 2, currentY, { align: "center" });
            currentY += lineHeight;
            doc.setFontSize(8);
          } else {
            doc.setFontSize(7);
            doc.text("No se pudo generar barcode.", 5, currentY);
            currentY += lineHeight;
            doc.setFontSize(8);
          }
        } else {
          doc.setFontSize(7);
          doc.text(
            categoryTotal.barcode?.reason === "amount"
              ? "monto excede codigo"
              : "categoria sin barcode",
            5,
            currentY
          );
          currentY += lineHeight;
          doc.setFontSize(8);
        }
      });
    }

    currentY += 4;
    doc.setFontSize(10);
    doc.text("ChamaFruit", docWidth / 2, currentY, { align: "center" });

    pdfRef.current = doc;
    const pdfBlob = doc.output("blob");
    const pdfBlobUrl = URL.createObjectURL(pdfBlob);
    setPdfUrl(pdfBlobUrl);
  };

  const triggerLocalPrint = () => {
    if (!pdfUrl) return false;
    const iframeWindow = isPdfLoaded ? pdfIframeRef.current?.contentWindow : null;
    if (iframeWindow) {
      iframeWindow.focus();
      iframeWindow.print();
      return true;
    }
    const printWindow = window.open(pdfUrl);
    if (printWindow) {
      printWindow.addEventListener("load", () => {
        printWindow.focus();
        printWindow.print();
      });
      return true;
    }
    return false;
  };

  const showPrintNotification = (useRemotePrint) => {
    const notification = document.createElement("div");
    notification.textContent = useRemotePrint
      ? "Imprimiendo comprobante..."
      : "Abriendo comprobante en el navegador...";
    notification.style.position = "fixed";
    notification.style.bottom = "20px";
    notification.style.right = "20px";
    notification.style.backgroundColor = "#4caf50";
    notification.style.color = "white";
    notification.style.padding = "10px";
    notification.style.borderRadius = "5px";
    notification.style.boxShadow = "0px 4px 6px rgba(0, 0, 0, 0.1)";
    document.body.appendChild(notification);

    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  };

  const runPostPrintFlow = (sourcePurchase, delayMs = 0) => {
    clearPendingPostPrintFlow();
    const openModalAndRedirect = () => {
      postPrintFlowTimerRef.current = null;
      openCategoryBarcodeModal(sourcePurchase);
      schedulePrintRedirect();
    };
    if (delayMs > 0) {
      postPrintFlowTimerRef.current = setTimeout(openModalAndRedirect, delayMs);
      return;
    }
    openModalAndRedirect();
  };

  const handlePrint = async (options = {}) => {
    const { postPrintDelayMs = 0, skipPostPrintFlow = false } = options || {};
    try {
      if (!canReadSales && !canUpdateSale) return false;
      const isSuspended = purchase?.saleStatus === "suspended";
      if (isSuspended) {
        if (!triggerLocalPrint()) {
          notify("No se pudo abrir la impresion local. Revisa el bloqueador de ventanas emergentes.");
          return false;
        }
        return true;
      }
      const shouldMarkPaid =
        !previewOnly && !isSuspended && !isPaid && purchase?.paymentTerm !== "credit";
      if (shouldMarkPaid && !canUpdateSale) return false;
      if (shouldMarkPaid) {
        // Ensure the purchase is marked as paid before printing (non-credit only)
        await markAsPaid(branchId, purchaseId);
        setIsPaid(true);
      }

      const printMode = getPrintMode();
      const useRemotePrint = printMode === PRINT_MODE_REMOTE;
      if (useRemotePrint) {
        sendPrintRequest(branchId, purchaseId);
      } else if (!triggerLocalPrint()) {
        notify("No se pudo abrir la impresion local. Revisa el bloqueador de ventanas emergentes.");
        return false;
      }

      showPrintNotification(useRemotePrint);
      if (!skipPostPrintFlow) {
        runPostPrintFlow(purchase, postPrintDelayMs);
      }
      return true;
    } catch (error) {
      console.error("Error updating purchase as paid:", error);
      notify("Error al actualizar la compra antes de imprimir.");
      return false;
    }
  };

  useEffect(() => {
    handlePrintRef.current = handlePrint;
  });

  useEffect(() => {
    if (!shouldAutoPrint || !purchase || !pdfUrl || !isPdfLoaded || autoPrintDoneRef.current) return;
    openCategoryBarcodeModal(purchase);
    autoPrintTimerRef.current = setTimeout(() => {
      autoPrintTimerRef.current = null;
      if (autoPrintDoneRef.current) return;
      autoPrintDoneRef.current = true;
      Promise.resolve(handlePrintRef.current?.({ skipPostPrintFlow: true }))
        .then((didPrint) => {
          if (didPrint) {
            schedulePrintRedirect();
          }
        });
    }, AUTO_PRINT_DELAY_MS);
    return () => {
      if (autoPrintTimerRef.current) {
        clearTimeout(autoPrintTimerRef.current);
        autoPrintTimerRef.current = null;
      }
    };
  }, [shouldAutoPrint, purchase, pdfUrl, isPdfLoaded]);

  const handleContinuePurchase = async () => {
    if (!purchase || !canUpdateSale) return;
    setIsResumingPurchase(true);
    try {
      if (purchase.saleStatus === "suspended") {
        await resumePurchase(branchId, purchaseId);
      } else {
        await reopenPurchase(branchId, purchaseId);
      }
      navigate(`/branches/${branchId}/purchases/${purchaseId}/edit`);
    } catch (error) {
      console.error("Error continuing purchase:", error);
      notify(error?.response?.data?.message || "No se pudo continuar la venta.");
      setIsResumingPurchase(false);
    }
  };

  const isSuspended = purchase?.saleStatus === "suspended";
  const saleStatus = String(purchase?.saleStatus || "").toLowerCase();
  const isCancelled = Boolean(purchase?.isDeleted) || saleStatus === "cancelled";
  const isClosedSale =
    saleStatus === "completed"
    || saleStatus === "closed"
    || (saleStatus !== "" && saleStatus !== "open" && saleStatus !== "suspended" && saleStatus !== "cancelled");
  const paidAmount = Number(purchase?.paidAmount || 0);
  const totalAmount = Number(purchase?.totalAmount || 0);
  const pendingBalance =
    purchase?.balance !== undefined
      ? Number(purchase.balance || 0)
      : Math.max(totalAmount - paidAmount, 0);
  const hasMissingPayment =
    pendingBalance > 0
    || purchase?.paymentStatus === "partial"
    || (!isPaid && totalAmount > 0);
  const canAddPayment =
    !previewOnly
    && canUpdateSale
    && !isSuspended
    && !isCancelled
    && isClosedSale
    && hasMissingPayment;
  const hasRecordedPayments = Array.isArray(purchase?.PurchasePayments)
    ? purchase.PurchasePayments.some((payment) => !payment?.isDeleted)
    : false;
  const isCreditPurchase =
    purchase?.paymentTerm === "credit"
    || purchase?.paymentMethod === "crédito"
    || Boolean(purchase?.detailForPayment?.creditDueDate || purchase?.detailForPayment?.creditExpiration);
  const canReopenCompletedPurchase =
    !previewOnly
    && canUpdateSale
    && !isSuspended
    && !isCancelled
    && saleStatus === "completed"
    && !isPaid
    && !isCreditPurchase
    && !hasRecordedPayments
    && paidAmount <= 0
    && pendingBalance > 0
    && purchase?.paymentStatus !== "partial";
  const canPrint = previewOnly || isSuspended || isPaid || purchase?.paymentTerm === "credit";
  const canContinuePurchase =
    !previewOnly
    && canUpdateSale
    && !isCancelled
    && (isSuspended || canReopenCompletedPurchase);
  const sourcePurchaseOrderId = fromPurchaseOrderParam || purchase?.detailForPayment?.purchaseOrderId;
  const shouldOfferDeliveryGuide =
    !previewOnly
    && Boolean(sourcePurchaseOrderId)
    && (offerDeliveryGuideParam === "1" || offerDeliveryGuideParam === "true");
  const title = previewOnly && !isSuspended ? "Comprobante Provisorio" : (isSuspended ? "Venta Suspendida" : "Comprobante de Venta");

  return (

    <>
      <div className="flex flex-col items-center p-6">
        <h1 className="text-lg font-bold mb-2 text-center">
          {title}
        </h1>
        {previewOnly && !isPaid && !isSuspended && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded mb-2">
            Vista previa: pago pendiente.
          </p>
        )}

        {shouldOfferDeliveryGuide && (
          <div className="mb-4 w-full max-w-[400px] rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <p className="font-semibold">Venta generada desde orden #{sourcePurchaseOrderId}</p>
            <p className="mt-1 text-xs text-emerald-800">
              Puedes crear la guia de despacho para esta venta cuando corresponda.
            </p>
            {canCreateDeliveryGuide ? (
              <button
                type="button"
                onClick={() => navigate(`/branches/${branchId}/purchases/${purchaseId}/delivery-guides/new`)}
                className="mt-3 w-full rounded bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
              >
                Crear guia de despacho
              </button>
            ) : (
              <p className="mt-2 text-xs text-emerald-800">
                No tienes permiso para crear guias de despacho.
              </p>
            )}
          </div>
        )}

        <div className="mb-4 flex items-center gap-2">
          <label htmlFor="width" className="text-sm font-semibold">Tamaño del recibo:</label>
          <select
            id="width"
            value={receiptWidth}
            onChange={(e) => {
              setReceiptWidth(e.target.value);
              if (purchase) generatePDF(purchase, e.target.value); // Regenerate on change
            }}
            className="border p-1 rounded text-sm"
          >
            <option selected value="80">80 mm</option>
            <option value="58">58 mm</option>
          </select>
        </div>

        {/* Mobile-Friendly PDF View */}
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            className="w-full max-w-[400px] h-[500px] border border-gray-300 shadow-lg"
            title="Receipt"
            ref={pdfIframeRef}
            onLoad={() => setIsPdfLoaded(true)}
          ></iframe>
        ) : (
          <p>
            <InlineLoading label="Cargando comprobante" />
          </p>
        )}

        {canAddPayment && (
          <button
            onClick={() => setIsPaymentModalOpen(true)}
            className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded text-sm w-full max-w-[300px]"
          >
            Agregar pago
            {pendingBalance > 0 ? ` ($${pendingBalance.toLocaleString("de-DE")} pendiente)` : ""}
          </button>
        )}

        {canContinuePurchase && (
          <button
            onClick={handleContinuePurchase}
            className={`mt-4 px-4 py-2 text-white rounded text-sm w-full max-w-[300px] ${isResumingPurchase ? 'bg-gray-400 cursor-not-allowed' : 'bg-yellow-500 hover:bg-yellow-600'}`}
            disabled={isResumingPurchase}
          >
            {isResumingPurchase ? "Continuando..." : "Continuar compra"}
          </button>
        )}

        {/* Print Button */}
        {(canReadSales || canUpdateSale) && (
          <button
            onClick={handlePrint}
            className={`mt-2 px-4 py-2 text-white rounded text-sm w-full max-w-[300px] ${canPrint ? 'bg-blue-500 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
            disabled={!canPrint}
          >
            Imprimir Recibo
          </button>
        )}
        {/* Download PDF Button */}
        {(canReadSales || canUpdateSale) && (
          <button
            onClick={() => {
              if (pdfRef.current && purchase) {
                const clientName = purchase.Client?.name?.replace(/\s+/g, "_") || "detalle";
                const fileName = `venta_${clientName}_${purchaseId}.pdf`;
                pdfRef.current.save(fileName);
              }
            }}
            className="mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm w-full max-w-[300px]"
          >
            Descargar PDF
          </button>
        )}
      </div>
      {isCategoryBarcodeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
          <div
            className="w-full max-w-5xl bg-white rounded-lg shadow-xl overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="category-barcode-modal-title"
          >
            <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div>
                <h2 id="category-barcode-modal-title" className="text-base font-semibold text-gray-900">
                  Barcodes por categoria
                </h2>
                <p className="text-xs text-gray-500">
                  Venta #{purchase?.id || purchaseId}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseCategoryBarcodeModal}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label="Cerrar modal de barcodes"
              >
                ×
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-4 py-3 space-y-3">
              {categoryBarcodeModalRedirectSeconds !== null ? (
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  Redireccion automatica al POS en {categoryBarcodeModalRedirectSeconds} segundos.
                </div>
              ) : (
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  La redireccion automatica esta desactivada. Puedes volver al POS con el boton inferior.
                </div>
              )}

              {categoryBarcodeModalRows.length ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {categoryBarcodeModalRows.map((categoryTotal) => {
                    const note = getCategoryBarcodeNote(categoryTotal);
                    return (
                      <div
                        key={categoryTotal.key}
                        className="rounded border border-gray-200 bg-white px-3 py-3"
                      >
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {categoryTotal.label}
                            </div>
                            <div className="text-xs text-gray-500">
                              Categoria ID: {categoryTotal.categoryId || "sin categoria"}
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            ${Number(categoryTotal.amount || 0).toLocaleString("de-DE")}
                          </div>
                        </div>

                        {categoryTotal.barcode?.isValid ? (
                          <Barcode
                            value={categoryTotal.barcode.value}
                            height={160}
                            width={4}
                            margin={32}
                            marginTop={4}
                            marginBottom={4}
                            className="mt-3"
                            svgClassName="w-[58mm] max-w-full"
                            valueClassName="mt-1 font-mono text-sm text-gray-700"
                          />
                        ) : (
                          <div className="mt-3 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            {note}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded border border-dashed border-gray-300 bg-gray-50 px-4 py-5 text-center text-sm text-gray-500">
                  No hay barcodes de categorias para esta venta.
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleCloseCategoryBarcodeModal}
                className="px-4 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
              >
                Seguir en comprobante
              </button>
              <button
                type="button"
                onClick={handleGoBackToPos}
                className="px-4 py-2 rounded bg-blue-600 text-sm text-white hover:bg-blue-700"
              >
                Volver al POS
              </button>
            </div>
          </div>
        </div>
      )}
      {!previewOnly && (
        <PaymentModal
          isOpen={isPaymentModalOpen && canUpdateSale}
          purchaseData={purchase}
          onClose={() => setIsPaymentModalOpen(false)}
          onConfirmPayment={handleConfirmPayment}
          canReadClient={canReadClient}
          canCreateClient={canCreateClient}
          onContinuePurchase={canContinuePurchase ? handleContinuePurchase : undefined}
          isContinuingPurchase={isResumingPurchase}
        />
      )}
    </>
  );
};

export default PurchaseReceiptPage;
